/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/unbound-method */
import { isPlatformBrowser } from '@angular/common';
import { catchError, concatMap, EMPTY, firstValueFrom, from, map, of, throwError, type Observable } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GsIconListComponent } from '../../shared/components/gs-icon-list/gs-icon-list.component';
import { GSIconListBulletModel, GSIconListModel } from '../../shared/components/gs-icon-list/gs-icon-list.model';
import { StorageEnum } from '../../shared/enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Component, Inject, type OnInit, PLATFORM_ID, Renderer2, TemplateRef, ViewChild } from '@angular/core';

import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../../shared/services/util.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../../shared/services/storage.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CookieService } from 'ngx-cookie-service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../shared/services/cms.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AuthService } from '../../shared/services/auth.service';
import { MobileAppBannerComponent } from '../../shared/components/mobile-app-banner/mobile-app-banner.component';
import { Nav } from '../../shared/enums/nav.enum';
import { isValid, isWithinInterval, max as maxDate, parseISO, subDays } from 'date-fns';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { SeoService } from '../../shared/services/seo.service';
import { AccountDetail } from '../../shared/models/account-detail';
import { type ServiceAddress } from '../../shared/models/service-address';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AccountService } from '../../shared/services/account.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { PaymentService } from '../../shared/services/payment.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { BiometricsService } from '../../shared/services/biometrics.service';
import { NotificationComponent } from '../../shared/components/notification/notification.component';
import {
  BiometryErrorType,
  BiometryType,
  type CheckBiometryResult,
} from '@aparajita/capacitor-biometric-auth/dist/esm/definitions.js';
import { Device } from '@capacitor/device';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { EncryptionService } from '../../shared/services/encryption.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NotificationsService } from '../../shared/services/notifications.service';
import { NotificationType } from '../../shared/enums/notificationtype.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AlertsService } from '../../shared/services/alerts.service';
import { ErrorData } from '../../shared/models/error-data';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { LoggingService } from '../../shared/services/logging.service';
import { getDeepLinkSource, getDeepLinkUrl } from '../../shared/models/deep-link-payload.model';

/**
 * Login component for Gas South Online Account Service.
 * Handles user authentication including email/password login and biometric authentication
 * for mobile platforms (iOS/Android). Supports "Remember Me" functionality and
 * integrates with the CMS for dynamic content labels.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    GsIconListComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MobileAppBannerComponent,
    MatCheckboxModule,
    RouterModule,
    NotificationComponent,
    MatDialogModule,
  ],
})
export class LoginComponent implements OnInit {
  pageReady = false;
  loginForm!: FormGroup;
  apiErrors: any[] = [];
  apiError: string = '';
  isHover = false;
  isFocus = false;
  passwordVisible = false;
  formErrorMessage: string = '';
  showNewPasswordCreatedHeader: boolean = false;
  newPasswordCreatedUserName = '';
  showAccountDeletedHeader: boolean = false;
  isBrowser: boolean = false;
  accountDetail: AccountDetail = new AccountDetail();
  pageName = Nav.Login;
  pageLabels: any;
  showBiometricOption: boolean = false;
  enrollInBiometrics: boolean = false;
  encryptedCreds: string = '';
  bioIcon: string = 'fa-solid fa-fingerprint';
  passwordWasUpdated: boolean = false;
  passwordUpdatedForUsername: string = '';
  biometricsUpdated: boolean = false;
  showAppBanner: boolean = true;
  updateMandatory: boolean = false;

  @ViewChild('updateAppDialog', { static: true }) updateAppDialog!: TemplateRef<any>;

  constructor(
    private readonly alertsS: AlertsService,
    private readonly dialog: MatDialog,
    private readonly bioS: BiometricsService,
    private readonly cms: CmsService,
    private readonly auth: AuthService,
    private readonly formBuilder: FormBuilder,
    // eslint-disable-next-line @typescript-eslint/prefer-readonly
    private router: Router,
    private readonly utils: UtilService,
    private readonly storage: StorageService,
    private readonly cookieService: CookieService,
    private readonly renderer: Renderer2,
    private readonly route: ActivatedRoute,
    private readonly seoS: SeoService,
    private readonly loggingService: LoggingService,
    @Inject(PLATFORM_ID) platformId: string,
    private readonly acct: AccountService,
    private readonly payment: PaymentService,
    private readonly encryptionS: EncryptionService,
    private readonly notifiS: NotificationsService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Displays the app update dialog modal.
   * @param mandatory - If true, user cannot dismiss the dialog without updating
   */
  private showUpdateDialog(mandatory: boolean): void {
    this.updateMandatory = mandatory;
    const dialogConfig = {
      disableClose: true,
      panelClass: 'gs-dialog',
    };
    this.dialog.open(this.updateAppDialog, dialogConfig);
  }

  openAppStore(): void {
    const platform = this.bioS.bioInfo.Platform.toLowerCase();
    let storeUrl = '';
    if (platform === 'ios') {
      // iOS automatically opens App Store when navigating to apps.apple.com
      storeUrl = 'https://apps.apple.com/app/id1528960564';
    } else if (platform === 'android') {
      // Try market:// first, fallback handled by catch
      storeUrl = 'market://details?id=com.gassouth.gs.mobile';
      // storeUrl = 'https://play.google.com/store/apps/details?id=com.gassouth.gs.mobile';
    }
    if (storeUrl) {
      window.location.href = storeUrl;
    }
  }

  /**
   * Initializes the login component.
   * Logs out any existing session, fetches CMS content for page labels,
   * sets up the login form, checks for remembered credentials, and
   * initializes biometric authentication options on supported platforms.
   */
  ngOnInit(): void {
    this.auth.logout(false);

    this.cms
      .getContent('oas-login', 'Login')
      .pipe(
        map((data) => {
          const listData = new GSIconListModel();
          listData.Header = data.accountBenefitsList.header;
          listData.Subheader = data.accountBenefitsList.subheader;

          const bulletList: GSIconListBulletModel[] = [];
          data.accountBenefitsList?.bulletList?.forEach((bullet: any) => {
            const bulletObj = new GSIconListBulletModel();
            bulletObj.BulletText = bullet.bullet.bulletText;
            bulletObj.BulletTitle = bullet.bullet.bulletTitle;
            bulletObj.IconPath = bullet.bullet.iconPath;
            bulletObj.IconAltText = bullet.bullet.iconAltText;
            bulletObj.ShowBullet = bullet.bullet.showBullet;

            bulletList.push(bulletObj);
          });

          listData.BulletList = bulletList;

          const forgotLink = this.cms.getDataFromBuilderReference(data.forgotUsernamePasswordLinkUrl)?.url;
          const registerLink = this.cms.getDataFromBuilderReference(data.registerLinkUrl)?.url;

          const newDataObj = JSON.parse(JSON.stringify(data));
          newDataObj.accountBenefitsList = listData;
          newDataObj.forgotUsernamePasswordLinkUrl = forgotLink;
          newDataObj.registerLinkUrl = registerLink;

          newDataObj.biometricsPopup = this.cms.flattenBuilderReferencesPreservingKeys(data.biometricsPopup);

          this.seoS.setSeoTags(data.seo);

          this.pageLabels = newDataObj;
          return newDataObj;
        }),
        concatMap(() => {
          if (this.isBrowser) {
            // Wait for storage cache to be ready on native platforms before reading
            return from(this.storage.whenReady()).pipe(
              concatMap(() => {
                if (this.checkForNewPasswordCreated()) {
                  this.showNewPasswordCreatedHeader = true;
                }

                if (this.checkForDeletedAccount()) {
                  this.showAccountDeletedHeader = true;
                }

                this.loginForm = this.formBuilder.group({
                  email: [
                    this.showNewPasswordCreatedHeader ? this.newPasswordCreatedUserName : '',
                    [Validators.required],
                  ],
                  password: ['', [Validators.required, Validators.maxLength(12), Validators.minLength(7)]],
                  rememberMe: [false],
                  biometrics: [false],
                });
                // Check if remembered username exists (localStorage for native, cookie for web)
                // Check localStorage first (for native platforms), then cookie (for web)
                // Only apply remembered username if NOT coming from new password creation flow
                const rememberedFromStorage = this.storage.getLocal(StorageEnum.RememberedUsername);
                const rememberedFromCookie = this.cookieService.check('rememberedUsername')
                  ? this.cookieService.get('rememberedUsername')
                  : null;
                const rememberedUsername = rememberedFromStorage || rememberedFromCookie;
                if (rememberedUsername && !this.showNewPasswordCreatedHeader) {
                  this.loginForm.patchValue({
                    email: rememberedUsername,
                    rememberMe: true,
                  });
                }

                // Subscribe to biometrics changes to auto-enable rememberMe
                this.loginForm.get('biometrics')?.valueChanges.subscribe((biometricsEnabled: boolean) => {
                  const rememberMeControl = this.loginForm.get('rememberMe');
                  if (biometricsEnabled) {
                    rememberMeControl?.setValue(true);
                    rememberMeControl?.disable();
                  } else {
                    rememberMeControl?.enable();
                  }
                });

                // Add focus event listeners to reset the button state
                this.addFocusListeners();

                if (this.checkForNewPasswordCreated()) {
                  this.showNewPasswordCreatedHeader = true;
                }

                return from(Device.getInfo());
              }),
            );
          } else {
            return EMPTY;
          }
        }),
        concatMap((deviceInfo) => {
          this.bioS.bioInfo.Platform = deviceInfo.platform;
          if (deviceInfo.platform === 'ios' || deviceInfo.platform === 'android') {
            this.showAppBanner = false;
            // Check for updates before biometrics on mobile platforms
            return from(this.checkForUpdates()).pipe(
              concatMap((shouldContinue) => {
                if (!shouldContinue) {
                  // System is down, error page will be shown
                  return EMPTY;
                }
                return this.bioS.checkBioObs();
              }),
            );
          } else {
            this.showBiometricOption = false;
            this.pageReady = true;
            this.utils.hideSpinner();
            return EMPTY;
          }
        }),
      )
      .subscribe((res: CheckBiometryResult) => {
        if (res && res.code === BiometryErrorType.biometryNotAvailable) {
          this.bioS.bioInfo.BioAvailable = false;
          this.showBiometricOption = false;
        } else if (res && res.code === BiometryErrorType.biometryNotEnrolled) {
          // clear any stored encrypted creds since user is not enrolled in biometrics anymore
          this.storage.removeLocal(StorageEnum.EncryptedCreds);
          this.bioS.bioInfo.BioAvailable = true;
          this.bioS.bioInfo.BioTypes = res.biometryTypes;
          this.showBiometricOption = true;
        } else {
          this.bioS.bioInfo.BioAvailable = true;
          this.bioS.bioInfo.BioTypes = res.biometryTypes;
          this.pageLabels.biometricsLogInText = this.pageLabels.biometricsLogInText.replace(
            '{BIOTYPE}',
            this.bioS.getAvailableBiometricsText(),
          );
          this.bioIcon = this.bioS.getBioIcon(res.biometryTypes);

          this.showBiometricOption = true;
        }
        this.encryptedCreds = this.storage.getLocal(StorageEnum.EncryptedCreds);
        // Only attempt biometric auto-login if update is not required and system status is up
        const updateRequired = this.storage.get(StorageEnum.UpdateRequired) === true;
        const systemStatusDown = this.storage.get(StorageEnum.SystemStatusDown) === true;
        if (
          !updateRequired &&
          !systemStatusDown &&
          this.encryptedCreds &&
          this.email.valid &&
          !this.storage.getLocal(StorageEnum.BioModalShown)
        ) {
          this.storage.setLocal(StorageEnum.BioModalShown, true);
          this.onSubmit(true);
        } else {
          this.storage.setLocal(StorageEnum.BioModalShown, true);
        }
        this.pageReady = true;
        this.utils.hideSpinner();
      });
  }

  addFocusListeners(): void {
    if (typeof document !== 'undefined') {
      const emailField = document.getElementById('email');
      const passwordField = document.getElementById('LoginPassword');

      if (emailField && passwordField) {
        this.renderer.listen(emailField, 'focus', () => {
          this.resetButtonState();
        });
        this.renderer.listen(passwordField, 'focus', () => {
          this.resetButtonState();
        });
      }
    }
  }

  resetButtonState(): void {
    // New method
    this.isHover = false;
    this.isFocus = false;
  }

  getAup$ = (userLoginName: string, userPassword: string, isBioLogin: boolean = false): Observable<any> => {
    return this.auth.getAUP(userLoginName, userPassword).pipe(
      catchError((dataError: any) => {
        const errorCode = dataError.error.ErrorCode;

        // Check if error 30015 occurred during biometric login
        if (errorCode === '30015' && isBioLogin) {
          this.passwordWasUpdated = true;
          this.passwordUpdatedForUsername = userLoginName;
          // Persist to storage so it survives app restarts
          this.storage.setLocal(StorageEnum.PasswordUpdatedNeedsBioUpdate, userLoginName);
          this.apiError = 'Your password has been updated. Please log in using your updated credentials.';
          this.showAPIErrorLabel();
          this.utils.hideSpinner();
          return throwError(() => new Error(dataError));
        }

        const newApiError = this.apiErrors.find((error) => {
          return error.apiError.errorCode === errorCode;
        });
        if (newApiError === undefined) {
          const genericLoginError = this.apiErrors.find((error) => {
            return error.apiError.errorCode === 'GenericLoginError';
          });
          this.apiError = genericLoginError.apiError.displayMessage;
          this.showAPIErrorLabel();
        } else {
          this.apiError = newApiError.apiError.displayMessage;
          this.showAPIErrorLabel();
        }
        this.utils.hideSpinner();
        return throwError(() => new Error(dataError));
      }),
      concatMap((aupData: any) => {
        const acctNumber = aupData?.Accounts?.[0]?.AccountNumber;

        this.storage.removeSession(StorageEnum.SelectedSA);

        if (this.utils.isNullOrUndefined(acctNumber) || this.utils.isNullOrUndefined(userLoginName)) {
          return of(aupData);
        }

        return this.acct.getServiceAgreements(acctNumber, aupData?.Accounts?.[0]?.UserName).pipe(
          map((serviceAgreements: ServiceAddress[]) => {
            const selectedServiceAgreement = (serviceAgreements ?? [])
              .slice()
              .sort((a, b) => this.getLatestServiceAgreementDate(b) - this.getLatestServiceAgreementDate(a))[0];

            if (!this.utils.isNullOrUndefined(selectedServiceAgreement)) {
              this.storage.setSession(StorageEnum.SelectedSA, selectedServiceAgreement);
            }

            return aupData;
          }),
          catchError(() => of(aupData)),
        );
      }),
    );
  };

  private parseDateTime(input?: string | undefined): Date | null {
    const trimmedInput = input?.trim();
    if (!trimmedInput) {
      return null;
    }

    const isoParsedDate = parseISO(trimmedInput);
    if (isValid(isoParsedDate)) {
      return isoParsedDate;
    }

    const parsedDate = new Date(trimmedInput);
    return isValid(parsedDate) ? parsedDate : null;
  }

  private getLatestServiceAgreementDate(serviceAgreement: ServiceAddress): number {
    if (!serviceAgreement?.SaEndDate?.trim()) {
      return Number.MAX_SAFE_INTEGER;
    }

    const validDates = [
      this.parseDateTime(serviceAgreement?.SaStartDate),
      this.parseDateTime(serviceAgreement?.SaEndDate),
      this.parseDateTime(serviceAgreement?.BillingRateExpirationDate),
    ].filter((date): date is Date => date !== null);

    return validDates.length > 0 ? maxDate(validDates).getTime() : 0;
  }

  private getSingleAccountRoute(statusFlag: string, saEndDate: string | undefined, seasonal: boolean): Observable<any> {
    switch (statusFlag) {
      case '10':
        return of(Nav.PendingStart);
      case '60':
      case '70': {
        const endDate = saEndDate ? new Date(saEndDate) : null;
        const inInterval =
          endDate !== null &&
          !Number.isNaN(endDate.getTime()) &&
          isWithinInterval(endDate, {
            start: subDays(new Date(), 240),
            end: new Date(),
          });
        return of(seasonal && inInterval ? Nav.Seasonal : Nav.Closed);
      }
      case '20':
      case '40':
      case '50':
      default:
        if (this.isBrowser) {
          const deepLinkRedirect = getDeepLinkUrl(this.storage.getSession(StorageEnum.DeepLink));
          if (!this.utils.isNullOrUndefined(deepLinkRedirect)) {
            return this.getAccountData();
          }
        }
        return of(Nav.AccountSummary);
    }
  }

  get email(): FormControl {
    return this.loginForm.get('email') as FormControl;
  }

  get password(): FormControl {
    return this.loginForm.get('password') as FormControl;
  }

  checkForNewPasswordCreated(): boolean {
    const queryParams = this.route.snapshot.queryParams;
    const newPasswordCreated = queryParams['newPasswordCreated'];
    const newPasswordCreatedUserName = queryParams['userName'];
    if (this.utils.isNullOrUndefined(newPasswordCreated) || this.utils.isNullOrUndefined(newPasswordCreatedUserName)) {
      return false;
    }
    if (newPasswordCreated === 'true') {
      this.showNewPasswordCreatedHeader = true;
      this.newPasswordCreatedUserName = newPasswordCreatedUserName;
      return true;
    }
    return false;
  }

  checkForDeletedAccount(): boolean {
    const queryParams = this.route.snapshot.queryParams;
    const accountDeleted = queryParams['accountDeleted'];
    if (this.utils.isNullOrUndefined(accountDeleted)) {
      return false;
    }
    if (accountDeleted === 'true') {
      return true;
    }
    return false;
  }

  /**
   * Handles login form submission.
   * Validates the form, processes "Remember Me" preference, and authenticates
   * the user via API or biometric credentials. On success, navigates to the
   * appropriate dashboard or deep-linked destination.
   * @param hasBio - If true, attempts biometric authentication instead of password
   */
  onSubmit(hasBio: boolean = false): void {
    this.utils.showSpinner();

    this.apiError = '';
    this.formErrorMessage = '';
    this.notifiS.clearAllNotifications(this.pageName);

    if (hasBio) {
      const isBioLockedOut = this.storage.getLocal(StorageEnum.BioLockout);
      if (isBioLockedOut) {
        const error = this.bioS.bioErrors.find((err) => err.errorCode === BiometryErrorType.biometryLockout);
        this.notifiS.addNotification(error.displayMessage, NotificationType.Alert, this.pageName);
        this.utils.hideSpinner();
        return;
      }
      this.loginForm.get('password')?.clearValidators();
    } else {
      this.loginForm
        .get('password')
        ?.setValidators([Validators.required, Validators.maxLength(12), Validators.minLength(7)]);
    }
    this.loginForm.get('password')?.updateValueAndValidity();

    if (this.email.valid && (this.loginForm.valid || hasBio)) {
      this.apiErrors = this.storage.getSession(StorageEnum.GlobalVars).apiErrors;
      // Handle form submission
      const userLoginName: string = this.email.value;
      const userPassword: string = this.password.value;

      // Check if password was recently updated and biometrics need updating
      const needsBioUpdate = this.storage.getLocal(StorageEnum.PasswordUpdatedNeedsBioUpdate);
      if (needsBioUpdate && !hasBio) {
        this.passwordWasUpdated = true;
        this.passwordUpdatedForUsername = needsBioUpdate;
        // Note: Don't remove from storage yet - only remove after successful update
      }

      if (Boolean(this.loginForm.getRawValue().rememberMe) && userLoginName.length > 0) {
        // Use localStorage on native platforms (iOS/Android) since cookies don't persist in WKWebView
        // Use cookies on web for better cross-tab support
        if (this.bioS.bioInfo.Platform === 'ios' || this.bioS.bioInfo.Platform === 'android') {
          this.storage.setLocal(StorageEnum.RememberedUsername, userLoginName);
          // Clean up any old cookie
          this.cookieService.delete(
            'rememberedUsername',
            '/',
            undefined,
            window.location.protocol === 'https:',
            'Strict',
          );
        } else {
          this.cookieService.set('rememberedUsername', userLoginName, {
            expires: 400,
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'Strict',
          }); // Max browser-allowed expiration
        }
      } else {
        // Delete remembered username if remember me is unchecked or username is not provided
        this.storage.removeLocal(StorageEnum.RememberedUsername);
        this.cookieService.delete(
          'rememberedUsername',
          '/',
          undefined,
          window.location.protocol === 'https:',
          'Strict',
        );
      }

      const aup$ = this.getAup$(userLoginName, userPassword, hasBio);

      const defaultBioResult: CheckBiometryResult = {
        code: BiometryErrorType.biometryNotAvailable,
        isAvailable: false,
        strongBiometryIsAvailable: false,
        biometryType: BiometryType.none,
        biometryTypes: [],
        deviceIsSecure: false,
        reason: '',
      };
      // Only call biometrics plugin when on mobile platform
      const bioCheck$ = this.showBiometricOption ? this.bioS.checkBioObs() : of(defaultBioResult);

      bioCheck$
        .pipe(
          concatMap((result: CheckBiometryResult) => {
            if (this.showBiometricOption && (hasBio || this.loginForm.get('biometrics')?.value)) {
              switch (result.code) {
                case BiometryErrorType.biometryNotEnrolled:
                  this.storage.removeLocal(StorageEnum.EncryptedCreds);
                  this.encryptedCreds = '';
                  this.bioS.handleError(result, this.pageName);
                  this.loginForm.get('biometrics')?.setValue(false);
                  this.utils.hideSpinner();
                  return EMPTY;
                case BiometryErrorType.none:
                  // check if encrypted creds are stored if yes call showBioAuth else proceed with login and enroll in biometrics for next time
                  if (this.encryptedCreds) {
                    // decrypt creds and call bio auth
                    return this.bioS.showBiometryPrompt(this.pageName, this.pageLabels.biometricsPopup).pipe(
                      catchError((err) => {
                        this.bioS.handleError(null, this.pageName, err);
                        this.utils.hideSpinner();
                        return EMPTY;
                      }),
                    );
                  } else {
                    this.enrollInBiometrics = true;
                    return aup$;
                  }

                default:
                  // For other errors, don't proceed
                  this.bioS.handleError(result, this.pageName);
                  return EMPTY;
              }
            } else {
              return aup$;
            }
          }),
          concatMap((aupData) => {
            if (hasBio) {
              // User is using bio for login
              return this.encryptionS.decrypt(this.encryptedCreds, this.loginForm.get('email')?.value).pipe(
                catchError((err) => {
                  this.utils.consoleLog('Decryption failed:' + JSON.stringify(err));
                  const error = this.bioS.bioErrors.find((err) => err.errorCode === 'decryptionError');
                  this.notifiS.addNotification(error.displayMessage, NotificationType.Alert, this.pageName);
                  this.utils.hideSpinner();
                  return EMPTY;
                }),
              );
            } else if (this.encryptedCreds) {
              // in case user has bio but uses pw for login.
              // need to check if they own the creds to display correct info on manage-account/login-info
              return this.encryptionS.decrypt(this.encryptedCreds, this.loginForm.get('email')?.value).pipe(
                catchError(() => {
                  return of(aupData);
                }),
                map((encData) => {
                  if (encData.Success === true && encData.PlainText) {
                    this.bioS.bioInfo.UsernameMatch = true;
                  } else {
                    this.bioS.bioInfo.UsernameMatch = false;
                  }
                  return 'credsChecked';
                }),
              );
            } else {
              return of(aupData);
            }
          }),
          concatMap((data) => {
            if (data === 'credsChecked') {
              return aup$;
            }
            // if coming from biometric auth - data will already be the aup response, else we need to call aup
            if (data.Success === true && data.PlainText) {
              const credsObj = JSON.parse(data.PlainText);
              this.bioS.bioInfo.UsernameMatch = true;
              return this.getAup$(credsObj.UserName, credsObj.Password, true);
            } else if (data.Success === false && data.PlainText) {
              this.bioS.bioInfo.UsernameMatch = false;
              const error = this.bioS.bioErrors.find((error) => error.errorCode === 'usernameMismatch');
              this.notifiS.addNotification(error.displayMessage, NotificationType.Alert, this.pageName);

              this.utils.hideSpinner();
              return EMPTY;
            } else {
              this.bioS.bioInfo.UsernameMatch = false;
              return of(data);
            }
          }),
          concatMap((aupData: any) => {
            const account = aupData.Accounts[0].ServiceAddresses[0];
            const selectedSA = this.storage.getSession(StorageEnum.SelectedSA) as ServiceAddress | undefined;
            const currentSA = selectedSA ?? account;
            const statusFlag = currentSA?.SaStatusFlag ?? '';
            // If password was updated and user logged in successfully, update encrypted credentials
            // Only update if the logged-in user matches the user whose password was updated
            if (this.passwordWasUpdated && !hasBio && this.bioS.bioInfo.BioAvailable) {
              const userLoginName: string = this.email.value;
              const userPassword: string = this.password.value;

              // Security check: Only update biometrics if this is the same user whose password was updated
              if (userLoginName.toLocaleLowerCase() === this.passwordUpdatedForUsername.toLocaleLowerCase()) {
                return this.encryptionS.encrypt(userLoginName, userPassword).pipe(
                  catchError((error) => {
                    this.utils.consoleGroup('Encryption error after password update:', error);
                    // Continue with navigation even if encryption fails
                    return of(null);
                  }),
                  map((encData: any) => {
                    if (encData?.EncryptedText) {
                      this.storage.setLocal(StorageEnum.EncryptedCreds, encData.EncryptedText);
                      this.bioS.bioInfo.UsernameMatch = true;
                      this.passwordWasUpdated = false;
                      this.passwordUpdatedForUsername = '';
                      // Clear the persisted flag now that biometrics are updated
                      this.storage.removeLocal(StorageEnum.PasswordUpdatedNeedsBioUpdate);
                      this.biometricsUpdated = true;
                    }
                    return aupData;
                  }),
                  concatMap((data) => {
                    if (aupData.TotalNumAccounts > 1) {
                      this.setupCustomerCookie(statusFlag, true);
                      return of(Nav.MultiAccountSummary);
                    } else {
                      this.setupCustomerCookie(statusFlag, false);
                      return this.getSingleAccountRoute(
                        statusFlag,
                        currentSA?.SaEndDate ?? account?.SaEndDate,
                        Boolean(aupData.Accounts[0].Seasonal),
                      );
                    }
                  }),
                );
              }
              // If username doesn't match, fall through to normal flow
            }

            if (aupData.TotalNumAccounts > 1) {
              this.setupCustomerCookie(statusFlag, true);
              return of(Nav.MultiAccountSummary);
            } else {
              this.setupCustomerCookie(statusFlag, false);
              return this.getSingleAccountRoute(
                statusFlag,
                currentSA?.SaEndDate ?? account?.SaEndDate,
                Boolean(aupData.Accounts[0].Seasonal),
              );
            }
          }),
        )
        .subscribe((data: any) => {
          let redirectRoute = '';
          this.storage.removeLocal(StorageEnum.BioLockout);

          if (typeof data === 'string') {
            redirectRoute = data;
          } else {
            data.FirstName = this.accountDetail.FirstName;
            data.LastName = this.accountDetail.LastName;
            this.accountDetail.Person = data;
            this.storage.setSession(StorageEnum.AccountDetail, this.accountDetail);
            const storedDeepLink = this.storage.getSession(StorageEnum.DeepLink);
            const parsedDeepLink = getDeepLinkUrl(storedDeepLink);
            const deepLinkSource = getDeepLinkSource(storedDeepLink);
            const deepLinkRedirect = parsedDeepLink ?? Nav.Login;
            if (!this.utils.isNullOrUndefined(parsedDeepLink)) {
              this.loggingService.logInfo(
                `Replaying stored deep link after login. source=${deepLinkSource}, path=${deepLinkRedirect.split('?')[0]}`,
              );
              this.storage.removeSession(StorageEnum.DeepLink);
              this.storage.hasDeepLinkRedirect.set(false);
            }
            redirectRoute = deepLinkRedirect;
          }

          // Add notification if biometrics were just updated
          if (this.biometricsUpdated) {
            this.notifiS.addNotification(
              this.pageLabels.bioUpdatedNotification.replace('{BIOTYPE}', this.bioS.getAvailableBiometricsText()),
              NotificationType.Postnotification,
              redirectRoute,
            );
            this.biometricsUpdated = false;
          }

          // Request push notification permission after login (account info now available)
          // This blocks navigation until user responds to permission prompt
          void this.requestPushAndNavigate(redirectRoute);
        });
    } else {
      // Mark all controls as touched to trigger validation messages
      this.loginForm.markAllAsTouched();
      this.showFormErrorLabel();
      this.utils.hideSpinner();
    }
  }

  logDebug(data: any): void {
    this.utils.consoleGroup('Login icon-list Debug Data', data);
  }

  async goToForgotPassword(): Promise<void> {
    await this.router.navigateByUrl(Nav.Forgot);
  }

  logButtonClick(data: any): void {
    this.utils.consoleGroup('logButtonClick', data);
  }

  logDebugData(data: any): void {
    this.utils.consoleGroup('logDebugData', data);
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  showFormErrorLabel(): void {
    this.formErrorMessage = this.storage.getSession(StorageEnum.GlobalVars).formError;
    if (this.formErrorMessage !== '') {
      this.notifiS.clearAllNotifications(this.pageName);
      this.notifiS.addNotification(this.formErrorMessage, NotificationType.Error, this.pageName);
    }
  }

  showAPIErrorLabel(): void {
    if (!this.utils.isNullOrUndefined(this.apiError) && this.apiError !== '' && this.apiError) {
      this.notifiS.clearAllNotifications(this.pageName);
      this.notifiS.addNotification(this.apiError, NotificationType.Error, this.pageName);
    }
  }

  getAccountData(): Observable<any> {
    const acct = this.storage.getSession(StorageEnum.LoginResponse);
    const accountNumber = acct?.Accounts[0]?.AccountNumber;

    return this.acct.getAccountDetail(accountNumber, acct.UserName).pipe(
      concatMap((data: AccountDetail) => {
        this.accountDetail = data;
        return this.payment.GetFuturePayment(accountNumber);
      }),
      concatMap((data: any) => {
        this.accountDetail.FuturePayment = data;
        if (acct.Accounts[0].ServiceAddresses[0].SaStatusFlag === '10') {
          // if SaStatusCode = Pending Start - set headline to 10 = Pending Start,
          return of('10');
        } else {
          return this.acct.GetHeadlineCode(accountNumber);
        }
      }),
      concatMap((data: string) => {
        this.accountDetail.HeadlineCode = this.utils.isNullOrUndefined(data) ? data : data.toString();
        if (this.accountDetail.IsAutoPay) {
          return this.acct.GetAutoPayDetails(accountNumber);
        } else {
          return of('no autopay');
        }
      }),
      concatMap((data: any) => {
        if (data !== 'no autopay') {
          this.accountDetail.AutoPay = data;
        }

        if (this.accountDetail.IsPayPlan) {
          return this.acct.GetPayPlanDetails(accountNumber);
        } else {
          return of('no payplan');
        }
      }),
      concatMap((data: any) => {
        if (data !== 'no payplan') {
          this.accountDetail.PayPlanAmount = data.PayPlanAmount;
          this.accountDetail.PayPlanDueDate = data.PayPlanDueDate;
          this.accountDetail.PayPlanType = data.PayPlanType;
          this.accountDetail.PaymentPlans = data.PaymentPlans;
        }
        const personReq = {
          AccountNumber: accountNumber,
          UserName: this.accountDetail.PrimaryAccountHolder === true ? acct.UserName : '',
          PersonId: acct.PersonId,
        };
        return this.acct.GetPerson(personReq);
      }),
    );
  }

  /**
   * Check for app updates and system status on mobile platforms.
   * Returns true if app should continue to biometrics, false if redirecting to error page.
   */
  private async checkForUpdates(): Promise<boolean> {
    try {
      const devInfo = await this.alertsS.GetDeviceInfo();
      const res = await firstValueFrom(this.alertsS.registerDevice(devInfo));
      // if device token is invalid, clear storage fields
      if (res[0].ValidToken === false) {
        this.storage.removeLocal(StorageEnum.NotificationTokenValue);
      }

      if (res[0].SystemStatus === false) {
        this.storage.set(StorageEnum.SystemStatusDown, true);
        await this.loadApiErrorPage();
        return false;
      } else {
        // Clear flag when system is up
        this.storage.remove(StorageEnum.SystemStatusDown);
      }

      if (res[0].DeviceStatus === 'RequiresUpdate') {
        this.storage.set(StorageEnum.UpdateRequired, true);
        this.showUpdateDialog(res[0].UpdateMandatory === true);
      } else {
        // Clear flag when no update required
        this.storage.remove(StorageEnum.UpdateRequired);
      }

      return true;
    } catch (error) {
      console.error('Login: Error during checkForUpdates: ', error);
      // Set system status down on error to prevent auto-login
      this.storage.set(StorageEnum.SystemStatusDown, true);
      return true; // Continue to show login page on error
    }
  }

  /**
   * Navigate to error page when system is down.
   */
  private async loadApiErrorPage(): Promise<void> {
    const err = this.storage
      .getSession(StorageEnum.GlobalVars)
      .apiErrors.find((error: any) => error.apiError.errorCode === 'AppError');
    const errorData = new ErrorData();
    if (!this.utils.isNullOrUndefined(err)) {
      errorData.Header = err.apiError.errorMessage;
      errorData.Subheader = err.apiError.displayMessage;
    } else {
      // Fallback error message when CMS content is not yet loaded
      errorData.Header = 'System Unavailable';
      errorData.Subheader = 'We are currently experiencing technical difficulties. Please try again later.';
    }
    this.storage.setSession(StorageEnum.ErrorData, errorData);
    await this.router.navigateByUrl(Nav.AppError);
  }

  /**
   * Request push notification permission after login, then navigate.
   * On native platforms, this requests permission before showing any modals.
   * For multi-account users, push permission is requested after account selection (in resolver).
   */
  private async requestPushAndNavigate(redirectRoute: string): Promise<void> {
    // Skip push permission request for multi-account users - will be requested after account selection
    const isMultiAccount = redirectRoute === Nav.MultiAccountSummary;

    // Request push permission on native platforms (uses account info)
    if (!isMultiAccount && (this.bioS.bioInfo.Platform === 'ios' || this.bioS.bioInfo.Platform === 'android')) {
      try {
        await this.alertsS.GetDeviceToken();
      } catch (error) {
        this.utils.consoleGroup('Error requesting push permission:', error);
        // Continue with navigation even if push permission fails
      }
    }

    // Navigate to the appropriate route
    if (this.enrollInBiometrics) {
      // store route and go to enrollment page
      // go to biometric enrollment page or show biometric enrollment prompt
      this.storage.setSession(StorageEnum.BioRedirectRoute, redirectRoute);
      await this.router.navigateByUrl(Nav.BioTerms);
    } else {
      await this.router.navigateByUrl(redirectRoute);
    }
  }

  setupCustomerCookie(statusFlag: string, multiAccount: boolean): void {
    const cookieName = 'userCustomerStatus';
    const domain = '.gassouth.com';

    if ((statusFlag === '60' || statusFlag === '70') && !multiAccount) {
      this.cookieService.delete(cookieName, '/', domain);
      return;
    }

    if (!this.cookieService.check(cookieName)) {
      this.cookieService.set(cookieName, 'true', {
        expires: 30,
        path: '/',
        domain,
        sameSite: 'Lax',
        secure: this.isBrowser && window.location.protocol === 'https:',
      });
    }
  }
}
